import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  ColumnDef,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { ReportColumn } from './types';

interface Props<T extends object> {
  data: T[];
  columns: ReportColumn<T>[];
}

export default function ReportDataTable<T extends object>({ data, columns }: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        accessorKey: col.accessorKey as string,
        header: col.header,
        cell: col.cell
          ? ({ row }) => col.cell!(row.original)
          : ({ getValue }) => {
              const val = getValue();
              return val == null ? '' : String(val);
            },
        meta: { align: col.align || 'left' },
      })),
    [columns],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <table className="w-full text-sm text-left">
      <thead className="text-xs text-gray-500 bg-gray-50 uppercase sticky top-0 border-b border-gray-100">
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((header) => {
              const align = (header.column.columnDef.meta as { align?: string })?.align;
              return (
                <th
                  key={header.id}
                  className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''} ${
                    header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                  }`}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getIsSorted() === 'asc' ? ' ↑' : ''}
                  {header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody className="divide-y divide-gray-100">
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-50/50">
            {row.getVisibleCells().map((cell) => {
              const align = (cell.column.columnDef.meta as { align?: string })?.align;
              return (
                <td
                  key={cell.id}
                  className={`px-4 py-3 ${align === 'right' ? 'text-right' : ''} text-gray-700`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function columnsToCsvHeaders<T>(columns: ReportColumn<T>[]) {
  return columns.map((c) => ({ key: c.id, label: c.header }));
}

export function rowsToCsvData<T extends object>(rows: T[], columns: ReportColumn<T>[]) {
  return rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const col of columns) {
      if (col.exportValue) {
        out[col.id] = col.exportValue(row);
      } else if (col.accessorKey) {
        out[col.id] = (row as Record<string, unknown>)[col.accessorKey as string];
      }
    }
    return out;
  });
}
